import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Capacitor } from '@capacitor/core';
import { ProfileService } from 'src/app/services/profile.service';
import { ExtraAddress, Profile } from '../../../models/register.models';
import { Regions } from '../../../other/regions';

@Component({
  selector: 'fun-profile-address-form',
  templateUrl: './profile-address-form.component.html',
  styleUrls: ['./profile-address-form.component.scss']
})
export class ProfileAddressFormComponent implements OnInit {

  @Input() profile: Profile;
  @Input() address: ExtraAddress;
  @Input() isEdit: boolean = false;
  @Input() isMobile: boolean = false;
  @Output() saveEvent = new EventEmitter<{ formData: any, selectedTypes: number[] }>();
  @Output() cancelEvent = new EventEmitter<void>();

  addressForm: FormGroup;
  formControlKeys: string[] = [];
  errorMessages: any = {};
  isSubmit: boolean = false;
  errorAmount: boolean = false;
  loading: boolean = false;

  step: number = 1;
  legalAccepted: boolean = false;
  legalError: boolean = false;
  successMessage: string = '';

  isNationalResidence: boolean = true;
  autocompleteMode: boolean = false;
  desktopApp: boolean = false;
  regionList: any[] = [];
  townList: any[] = [];
  countriesList: any[] = [];
  towns: any;
  selectedTypes: number[] = [];

  // 1 = Dirección del DNI
  // 2 = Dirección censal
  // 3 = Dirección de residencia
  // 4 = Vivienda en propiedad
  // 5 = Vivienda de alquiler
  // 6 = Dirección laboral
  addressTypes = [
    { id: 1, name: 'Dirección del DNI' },
    { id: 2, name: 'Dirección censal' },
    { id: 3, name: 'Dirección de residencia' },
    { id: 4, name: 'Vivienda en propiedad' },
    { id: 5, name: 'Vivienda de alquiler' },
    { id: 6, name: 'Dirección laboral' }
  ];

  private patternAddress = '^[A-Za-zÀ-ÿñÑ0-9\\s\\.\\,\\-\\/\\º\\ª\\#]+$';
  private patternAddressAutocomplete = '^[A-Za-z-ñÑáéíóúÁÉÍÓÚóºòöôõøœōÓºÒÖÔÕØŒŌáàäâãåąæāÁªÀÄÂÃÅĄÆĀéèëêęėēíïìîįīÍÏÌÎĮĪúüùûūÚÜÙÛŪçÇ\' ,.0123456789ºª/]+$';
  private patternCp = '^[0-9]+$';
  private patternCpInternational = '^[A-Za-z0-9\\s\\-]+$';

  constructor(
    private chdRef: ChangeDetectorRef,
    private profileService: ProfileService
  ) { }

  ngOnInit() {
    this.desktopApp = Capacitor.getPlatform() === 'web';
    this.initForm();
    this.loadRegions();
    this.loadCountries();

    if (this.address) {
      this.populateForm();
      this.autocompleteMode = false;
      // Cuando editamos, saltamos directamente al paso 2
      this.step = 2;
    } else {
      // Para nuevas direcciones nacionales, activar autocomplete por defecto
      this.autocompleteMode = this.isNationalResidence;
    }

    this.chdRef.detectChanges();
  }

  initForm(): void {
    this.addressForm = new FormGroup({
      addressAutocomplete: new FormControl('', [Validators.pattern(this.patternAddressAutocomplete)]),
      address: new FormControl('', [Validators.required, Validators.pattern(this.patternAddress)]),
      postalCode: new FormControl('', [Validators.required, Validators.pattern(this.patternCp)]),
      region: new FormControl(null, []),
      city: new FormControl(null, []),
      internationalRegion: new FormControl('', []),
      internationalCity: new FormControl('', []),
      country: new FormControl(108, [Validators.required])
    });

    this.formControlKeys = Object.keys(this.addressForm.controls);

    this.errorMessages = {
      required: 'Campo obligatorio',
      pattern: 'Formato incorrecto'
    };
  }

  isTypeSelected(typeId: number): boolean {
    return this.selectedTypes.includes(typeId);
  }

  toggleType(typeId: number): void {
    const index = this.selectedTypes.indexOf(typeId);
    if (index > -1) {
      this.selectedTypes.splice(index, 1);
    } else {
      this.selectedTypes.push(typeId);
    }
    this.chdRef.detectChanges();
  }

  populateForm(): void {
    if (!this.address) return;

    this.isNationalResidence = this.address.country === 108;
    this.selectedTypes = this.address.type ? [this.address.type] : [];

    this.addressForm.patchValue({
      address: this.address.address,
      postalCode: this.address.postalCode,
      region: this.address.region || null,
      city: this.address.city || null,
      internationalRegion: this.address.internationalRegion || '',
      internationalCity: this.address.internationalCity || '',
      country: this.address.country
    });

    if (this.isNationalResidence && this.address.region) {
      this.loadCities(this.address.region);
    }
  }

  loadRegions(): void {
    this.regionList = Regions.map(region => ({
      id: region.code,
      name: region.name
    }));
  }

  loadCountries(): void {
    this.profileService.getCountries().subscribe(
      (response: any) => {
        for (const country of response) {
          // Excluimos España (108) de la lista internacional
          if (country.code !== 108) {
            this.countriesList.push({
              id: country.code,
              name: country.name
            });
          }
        }
        this.chdRef.detectChanges();
      }
    );
  }

  loadCities(regionId: any): void {
    this.loading = true;
    this.profileService.getCities(108, regionId).subscribe(
      (resp: any) => {
        this.townList = resp.map((city: any) => ({
          id: city.id || city.name,
          name: city.name
        }));
        this.loading = false;
        this.chdRef.detectChanges();
      },
      () => {
        this.loading = false;
        this.chdRef.detectChanges();
      }
    );
  }

  onRegionChange(): void {
    const regionId = this.addressForm.get('region').value;
    if (regionId && this.isNationalResidence) {
      this.addressForm.get('city').setValue(null);
      this.loadCities(regionId);
    }
  }

  switchAutocomplete(mode: boolean): void {
    this.autocompleteMode = mode;
    if (mode) {
      // Limpiar campos manuales
      this.addressForm.get('address').setValue('');
      this.addressForm.get('postalCode').setValue('');
      this.addressForm.get('region').setValue(null);
      this.addressForm.get('city').setValue(null);
      this.townList = [];
    } else {
      // Limpiar campo autocomplete
      this.addressForm.get('addressAutocomplete').setValue('');
    }
    this.chdRef.detectChanges();
  }

  getAddress(value: any): void {
    this.autocompleteMode = false;
    this.chdRef.detectChanges();

    let numberAddress = '';
    let regionId = '';
    let cityName = '';

    value.address_components.forEach((element: any) => {
      switch (element.types[0]) {
        case 'street_number':
          numberAddress = element.long_name;
          break;

        case 'route':
        case 'town_square':
          const autoAddress = numberAddress !== '' ? element.long_name + ', ' + numberAddress : element.long_name;
          this.addressForm.get('address').setValue(autoAddress);
          break;

        case 'postal_code':
          this.addressForm.get('postalCode').setValue(element.long_name);
          break;

        case 'administrative_area_level_2':
          this.regionList.forEach(region => {
            if (region.name.indexOf(element.long_name) > -1) {
              regionId = region.id;
              this.addressForm.get('region').setValue(regionId);
            }
          });
          break;

        case 'locality':
          cityName = element.long_name;
          break;
      }
    });

    if (regionId && cityName) {
      this.changeRegionCity(regionId, cityName);
    }
    this.chdRef.detectChanges();
  }

  changeRegionCity(region: string, city: string): void {
    this.addressForm.get('city').setValue(null);
    this.profileService.getCities(108, region).subscribe((resp: any) => {
      this.townList = resp.map((c: any) => ({
        id: c.id || c.name,
        name: c.name
      }));
      // Buscar la ciudad por nombre
      this.townList.forEach((town: any) => {
        if (town.name.indexOf(city) === 0) {
          this.addressForm.get('city').setValue(town.id);
        }
      });
      this.chdRef.detectChanges();
    });
  }

  toggleCountryType(isNational: boolean): void {
    this.isNationalResidence = isNational;
    this.townList = [];

    if (isNational) {
      this.addressForm.get('country').setValue(108);
      this.addressForm.get('postalCode').setValidators([Validators.required, Validators.pattern(this.patternCp)]);
      this.addressForm.get('internationalRegion').clearValidators();
      this.addressForm.get('internationalCity').clearValidators();
      this.addressForm.get('internationalRegion').setValue('');
      this.addressForm.get('internationalCity').setValue('');
      // Activar autocomplete para direcciones nacionales nuevas
      if (!this.isEdit) {
        this.autocompleteMode = true;
      }
    } else {
      this.addressForm.get('country').setValue(null);
      this.addressForm.get('postalCode').setValidators([Validators.required, Validators.pattern(this.patternCpInternational)]);
      this.addressForm.get('region').setValue(null);
      this.addressForm.get('city').setValue(null);
      this.addressForm.get('internationalRegion').setValidators([Validators.required]);
      this.addressForm.get('internationalCity').setValidators([Validators.required]);
      this.addressForm.get('country').setValidators([Validators.required]);
      // Desactivar autocomplete para direcciones internacionales
      this.autocompleteMode = false;
    }

    this.addressForm.get('postalCode').updateValueAndValidity();
    this.addressForm.get('region').updateValueAndValidity();
    this.addressForm.get('city').updateValueAndValidity();
    this.addressForm.get('internationalRegion').updateValueAndValidity();
    this.addressForm.get('internationalCity').updateValueAndValidity();
    this.addressForm.get('country').updateValueAndValidity();
    this.chdRef.detectChanges();
  }

  goToStep2(): void {
    this.isSubmit = true;
    if (this.selectedTypes.length === 0) {
      this.chdRef.detectChanges();
      return;
    }
    if (!this.isNationalResidence && !this.addressForm.get('country').value) {
      this.chdRef.detectChanges();
      return;
    }
    this.isSubmit = false;
    this.step = 2;
    this.chdRef.detectChanges();
  }

  goToStep3(): void {
    this.isSubmit = true;

    // Validación manual para campos nacionales
    if (this.isNationalResidence) {
      const region = this.addressForm.get('region').value;
      const city = this.addressForm.get('city').value;
      if (!region || !city) {
        this.chdRef.detectChanges();
        return;
      }
    }

    if (this.addressForm.invalid) {
      this.chdRef.detectChanges();
      return;
    }

    this.isSubmit = false;
    this.legalError = false;
    this.step = 3;
    this.chdRef.detectChanges();
  }

  prevStep(): void {
    this.isSubmit = false;
    this.step = this.step - 1;
    if (this.step < 1) {
      this.step = 1;
    }
    this.chdRef.detectChanges();
  }

  onSubmit(): void {
    if (!this.legalAccepted) {
      this.legalError = true;
      this.chdRef.detectChanges();
      return;
    }

    // Paso 4: loader dentro de la modal
    this.step = 4;
    this.chdRef.detectChanges();

    const formValue = this.addressForm.value;

    const formData: any = {
      address: formValue.address,
      postalCode: formValue.postalCode,
      country: formValue.country
    };

    if (this.isNationalResidence) {
      formData.region = formValue.region;
      formData.city = formValue.city;
    } else {
      formData.internationalRegion = formValue.internationalRegion;
      formData.internationalCity = formValue.internationalCity;
    }

    this.saveEvent.emit({ formData, selectedTypes: this.selectedTypes.slice() });
  }

  showSuccess(message: string): void {
    this.successMessage = message;
    this.step = 5;
    this.chdRef.detectChanges();
  }

  showError(): void {
    this.step = 6;
    this.chdRef.detectChanges();
  }

  retryFromError(): void {
    this.step = 3;
    this.chdRef.detectChanges();
  }

  onCancel(): void {
    this.cancelEvent.emit();
  }

  toggleLegal(): void {
    this.legalAccepted = !this.legalAccepted;
    if (this.legalAccepted) {
      this.legalError = false;
    }
    this.chdRef.detectChanges();
  }
}
